import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateRewardPoints } from "../../Redux/Actions/propertyActions";

const RewardPointsPopup = ({ isOpen, onClose, propertyId }) => {
    const dispatch = useDispatch();
    const { property, loading } = useSelector((state) => state.properties);

    const [rewardForm, setRewardForm] = useState({
        property_id: propertyId,
        max_reward_points: "",
        min_reward_points: ""
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen && property?.data) {
            setRewardForm({
                property_id: propertyId,
                max_reward_points: property.data.max_reward_points || "",
                min_reward_points: property.data.min_reward_points || ""
            });
            setErrors({}); 
        }
    }, [isOpen, propertyId, property?.data]);

    useEffect(() => {
        const max = parseInt(rewardForm.max_reward_points) || 0;
        const min = parseInt(rewardForm.min_reward_points) || 0;
        const newErrors = {};

        if (rewardForm.max_reward_points && rewardForm.min_reward_points && min > max) {
            newErrors.max_reward_points = "Min cannot exceed Max";
            newErrors.min_reward_points = "Min cannot exceed Max";
        }

        setErrors(newErrors);
    }, [rewardForm.max_reward_points, rewardForm.min_reward_points]);

    const handleRewardPointsUpdate = async (e) => {
        e.preventDefault();

        // Final validation before submit
        const maxPoints = parseInt(rewardForm.max_reward_points) || 0;
        const minPoints = parseInt(rewardForm.min_reward_points) || 0;

        if (minPoints > maxPoints) {
            alert("Minimum points cannot be greater than maximum points!");
            return;
        }

        if (!rewardForm.max_reward_points || !rewardForm.min_reward_points) {
            alert("Please fill both fields!");
            return;
        }

        try {
            const result = await dispatch(updateRewardPoints(rewardForm));
            if (result?.statusCode === 200) {
                onClose();
            }
        } catch (error) {
            console.error("Reward points update failed:", error);
        }
    };

    const handleChange = (field, value) => {
        setRewardForm({ ...rewardForm, [field]: value });
        // Clear specific field error when typing
        if (errors[field]) {
            setErrors({ ...errors, [field]: null });
        }
    };

    const isEmpty = !rewardForm.max_reward_points && !rewardForm.min_reward_points;
    const hasErrors = errors.max_reward_points || errors.min_reward_points;
    const isValid = rewardForm.max_reward_points && rewardForm.min_reward_points && !hasErrors;

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
                zIndex: 1001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#ffffff',
                    borderRadius: '20px',
                    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
                    width: '450px',
                    maxWidth: '90vw',
                    position: 'relative',
                    animation: 'slideUp 0.3s ease-out',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '28px 32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'linear-gradient(#0d152b,#2c4791)',
                        color: 'white'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px'
                        }}
                    >
                        <div
                            style={{
                                width: '44px',
                                height: '44px',
                                background: 'linear-gradient(#0d152b,#2c4791)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h3
                            style={{
                                margin: 0,
                                fontSize: '22px',
                                fontWeight: 600,
                                letterSpacing: '-0.3px'
                            }}
                        >
                            {isEmpty ? 'Add Reward Points' : 'Set Reward Points'}
                        </h3>
                    </div>
                    <button
                        style={{
                            background: 'linear-gradient(#0d152b,#2c4791)',
                            border: 'none',
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            color: 'white'
                        }}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <div
                            style={{
                                position: 'absolute',
                                fontSize: '22px',
                                fontWeight: 'bold',
                                lineHeight: 1,
                                color: 'white'
                            }}
                        >
                            ×
                        </div>
                    </button>
                </div>

                {/* Divider */}
                <div
                    style={{
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)'
                    }}
                />

                {/* Form */}
                <form onSubmit={handleRewardPointsUpdate} style={{ padding: '32px' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '10px',
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '14px',
                            letterSpacing: '-0.1px'
                        }}>
                            Maximum Reward Points
                            <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <svg
                                style={{
                                    position: 'absolute',
                                    left: '16px',
                                    color: '#9ca3af',
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }}
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <input
                                name="max_reward_points"
                                type="number"
                                style={{
                                    width: '100%',
                                    padding: '14px 16px 14px 46px',
                                    border: errors.max_reward_points ? '2px solid #ef4444' : '2px solid #e5e7eb',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    color: '#1f2937',
                                    transition: 'all 0.2s ease',
                                    background: errors.max_reward_points ? '#fef2f2' : '#f9fafb',
                                    boxSizing: 'border-box'
                                }}
                                value={rewardForm.max_reward_points}
                                onChange={(e) => handleChange("max_reward_points", e.target.value)}
                                required
                                min="0"
                                placeholder="Enter maximum points"
                            />
                        </div>
                        {errors.max_reward_points && (
                            <p style={{
                                color: '#ef4444',
                                fontSize: '13px',
                                marginTop: '6px',
                                marginLeft: '4px'
                            }}>
                                {errors.max_reward_points}
                            </p>
                        )}
                    </div>

                    <div style={{ marginBottom: '32px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '10px',
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '14px',
                            letterSpacing: '-0.1px'
                        }}>
                            Minimum Reward Points
                            <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <svg
                                style={{
                                    position: 'absolute',
                                    left: '16px',
                                    color: '#9ca3af',
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }}
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <input
                                name="min_reward_points"
                                type="number"
                                style={{
                                    width: '100%',
                                    padding: '14px 16px 14px 46px',
                                    border: errors.min_reward_points ? '2px solid #ef4444' : '2px solid #e5e7eb',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    color: '#1f2937',
                                    transition: 'all 0.2s ease',
                                    background: errors.min_reward_points ? '#fef2f2' : '#f9fafb',
                                    boxSizing: 'border-box'
                                }}
                                value={rewardForm.min_reward_points}
                                onChange={(e) => handleChange("min_reward_points", e.target.value)}
                                required
                                min="0"
                                placeholder="Enter minimum points"
                            />
                        </div>
                        {errors.min_reward_points && (
                            <p style={{
                                color: '#ef4444',
                                fontSize: '13px',
                                marginTop: '6px',
                                marginLeft: '4px'
                            }}>
                                {errors.min_reward_points}
                            </p>
                        )}
                    </div>

                    {/* Buttons */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}
                    >
                        <button
                            type="button"
                            style={{
                                padding: '13px 28px',
                                borderRadius: '12px',
                                fontSize: '15px',
                                fontWeight: 600,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                letterSpacing: '-0.1px',
                                background: 'white',
                                color: '#6b7280',
                                border: '2px solid #e5e7eb',
                                opacity: loading ? 0.6 : 1
                            }}
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '13px 28px',
                                borderRadius: '12px',
                                fontSize: '15px',
                                fontWeight: 600,
                                cursor: loading || !isValid ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                letterSpacing: '-0.1px',
                                background: (loading || !isValid) ? '#9ca3af' : 'linear-gradient(#0d152b,#2c4791)',
                                color: 'white',
                                boxShadow: (loading || !isValid) ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.3)',
                                opacity: loading ? 0.6 : 1
                            }}
                            disabled={loading || !isValid}
                        >
                            {loading ? (
                                <>
                                    <span
                                        style={{
                                            width: '14px',
                                            height: '14px',
                                            border: '2px solid rgba(255, 255, 255, 0.3)',
                                            borderTopColor: 'white',
                                            borderRadius: '50%',
                                            animation: 'spin 0.6s linear infinite'
                                        }}
                                    />
                                    Updating...
                                </>
                            ) : (
                                isEmpty ? "Add Reward Points" : "Update Reward Points"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RewardPointsPopup;
